// Where a Pixally Prep download came from: the ad (or post, or partner link) that first brought the
// visitor, carried onto Prep's counted download link (https://prep.pixally.com/download...) and onto
// the link a phone sends to its computer ("Send to computer", components/PrepParts.jsx).
//
// THE LINK PREP'S SERVER READS
//   utm_source, utm_medium, utm_campaign  the visitor's original outside source. With none, the site's
//                                         own tags, as before: utm_source=pixally.com,
//                                         utm_medium=website, utm_campaign=<the placement's campaign>
//                                         (prep_page, get_prep, or their _sent forms).
//   fbclid                                Meta's click id, when the visitor came on one.
//   entry                                 the button the download began on (ENTRY in content/prep.js),
//                                         [a-z0-9_-] only. Never put in a UTM.
//   via=sent                              the page was opened from the link a phone sent. It never
//                                         replaces the source. The name is the one links already sent
//                                         carry.
//   ref, test                             passed on from the page's own address, unchanged.
//
// THE SOURCE
//   - Outside means anything but the site's own tags (utm_source=pixally.com). A visit with the site's
//     own tags is no source at all, so it can never overwrite one.
//   - fbclid with no utm_source is Meta, paid, campaign unknown: utm_source=meta, utm_medium=paid and
//     no utm_campaign. It never falls back to the site's own tags.
//   - First touch wins. A source already kept in this browser stays, whatever source a later visit
//     arrives on, until it expires 90 days after it was kept: Ryan wants the paid source that first
//     brought someone to be the one the download is credited to.
//
// CONSENT (Privacy Policy 4.4). The source is written to this browser's storage, and read back, only
// while the Meta Pixel may run for this visitor on this page, by the pixel's own rule (pixelAllowed in
// lib/adPrivacy.js, reported here by components/MetaPixel.jsx). Until that is known, or when it is not
// allowed, the source lives in memory for this page and in-app navigation only. When advertising is
// off for the visitor (Global Privacy Control, the opt-out, "Decline", or a consent region with no
// yes: adsDenied in lib/adPrivacy.js), the kept source is deleted, and the Meta click id is left off
// the links as well: it is the one part that identifies a click to Meta.
//
// BEFORE THE APP LOADS. The prerendered pages draw the download links with the site's own tags. The
// script with id "pxly-prep-attribution" in public/index.html (in the head of every page) rewrites
// them as they are clicked, by the same rules, in ES5. Change one and the other together:
// src/lib/prepAttribution.test.js runs that script against this module and fails when they disagree.
import { PREP_SOURCE_KEY, gpcEnabled, readAdChoices } from './adPrivacy.js';

export const INTERNAL_SOURCE = 'pixally.com';
export const SOURCE_DAYS = 90;
const SOURCE_TTL = SOURCE_DAYS * 24 * 60 * 60 * 1000;
const MAX_TAG = 120; // what Prep's server keeps of each tag
const MAX_CLICK_ID = 500;
const RECORD_VERSION = 1;

const clean = (value, max) => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text ? text.slice(0, max) : '';
};

export const isInternalSource = (utmSource) => clean(utmSource, MAX_TAG).toLowerCase() === INTERNAL_SOURCE;

// A source as this module keeps it: { utm_source, utm_medium?, utm_campaign?, fbclid? }, or null.
// Anything else (an old record, a hand-edited one) is cleaned to that shape or dropped.
export const sanitizeSource = (value) => {
  if (!value || typeof value !== 'object') return null;
  const source = clean(value.utm_source, MAX_TAG);
  if (!source || isInternalSource(source)) return null;
  const out = { utm_source: source };
  const medium = clean(value.utm_medium, MAX_TAG);
  const campaign = clean(value.utm_campaign, MAX_TAG);
  const fbclid = clean(value.fbclid, MAX_CLICK_ID);
  if (medium) out.utm_medium = medium;
  if (campaign) out.utm_campaign = campaign;
  if (fbclid) out.fbclid = fbclid;
  return out;
};

// The outside source in an address's query string, or null when it has none.
export const landingSource = (search) => {
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch (_) {
    return null;
  }
  const source = clean(params.get('utm_source'), MAX_TAG);
  const fbclid = clean(params.get('fbclid'), MAX_CLICK_ID);
  if (source && !isInternalSource(source)) {
    return sanitizeSource({
      utm_source: source,
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      fbclid,
    });
  }
  if (fbclid) return { utm_source: 'meta', utm_medium: 'paid', fbclid };
  return null;
};

export const withoutClickId = (source) => {
  if (!source || !source.fbclid) return source || null;
  const { fbclid, ...rest } = source;
  return rest;
};

// What is written to storage: the source and when it was first kept.
export const storedSourceRecord = (source, now) => JSON.stringify({ v: RECORD_VERSION, t: now, s: source });

// The source in a stored record, or null when there is none, it is unreadable, or it has expired.
export const parseStoredSource = (raw, now) => {
  if (!raw) return null;
  try {
    const record = JSON.parse(raw);
    if (!record || record.v !== RECORD_VERSION || typeof record.t !== 'number') return null;
    if (!(now - record.t < SOURCE_TTL) || record.t > now + 60 * 1000) return null;
    return sanitizeSource(record.s);
  } catch (_) {
    return null;
  }
};

// What the page's own address says about this visit, apart from its source.
export const pageFlags = (search) => {
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch (_) {
    return { via: '', ref: '', test: '' };
  }
  return {
    via: params.get('via') === 'sent' ? 'sent' : '',
    ref: clean(params.get('ref'), MAX_TAG),
    test: params.get('test') === '1' ? '1' : '',
  };
};

export const cleanEntry = (entry) =>
  String(entry || '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '')
    .slice(0, 64);

const addSource = (query, source) => {
  query.set('utm_source', source.utm_source);
  if (source.utm_medium) query.set('utm_medium', source.utm_medium);
  if (source.utm_campaign) query.set('utm_campaign', source.utm_campaign);
  if (source.fbclid) query.set('fbclid', source.fbclid);
};

// The query string of a download link. `source` is the outside source (or null), `campaign` the
// placement's campaign for the site's own tags (already its _sent form on a page opened from a sent
// link), `entry` the button.
export const downloadQuery = ({ source, campaign, entry, via, ref, test }) => {
  const query = new URLSearchParams();
  const outside = sanitizeSource(source);
  if (outside) addSource(query, outside);
  else {
    query.set('utm_source', INTERNAL_SOURCE);
    query.set('utm_medium', 'website');
    query.set('utm_campaign', campaign);
  }
  const button = cleanEntry(entry);
  if (button) query.set('entry', button);
  if (via === 'sent') query.set('via', 'sent');
  if (ref) query.set('ref', ref);
  if (test === '1') query.set('test', '1');
  return query.toString();
};

export const attributedDownloadHref = (base, visit) => `${base}?${downloadQuery(visit)}`;

// The link a phone sends to its computer: this page, marked via=sent, with the outside source (and
// the page's ref and test) so the download on the computer is credited to it.
export const attributedSendLink = (pageUrl, { source, ref, test } = {}) => {
  const query = new URLSearchParams();
  query.set('via', 'sent');
  const outside = sanitizeSource(source);
  if (outside) addSource(query, outside);
  if (ref) query.set('ref', ref);
  if (test === '1') query.set('test', '1');
  return `${pageUrl}?${query}`;
};

// ---------------------------------------------------------------------------
// In the browser: the source for this page and in-app navigation, and what storage may hold.
// ---------------------------------------------------------------------------
let landing; // undefined until first read; then the page's outside source, or null
let report = null; // { allowed, denied } from components/MetaPixel.jsx, once it knows
let version = 0;
const listeners = new Set();

const inBrowser = () => typeof window !== 'undefined';
const storage = () => {
  try {
    return inBrowser() ? window.localStorage : null;
  } catch (_) {
    return null;
  }
};

const changed = () => {
  version += 1;
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (_) {
      /* one listener must not stop the others */
    }
  });
};

// The source the page was opened on. The inline script read it when the document loaded, before
// any in-app navigation could change the address; without it, the address is read here.
const pageLanding = () => {
  if (landing === undefined) {
    let fromScript = null;
    try {
      const early = inBrowser() && window.pxlyPrepAttribution;
      fromScript = early ? sanitizeSource(early.landing) : null;
    } catch (_) {
      fromScript = null;
    }
    landing = fromScript || (inBrowser() ? landingSource(window.location.search) : null);
  }
  return landing;
};

// A page reached by in-app navigation may carry a source when the page first opened had none.
export const rememberLanding = (search) => {
  if (pageLanding()) return;
  landing = landingSource(search);
  if (landing) changed();
};

export const removeStoredSource = () => {
  try {
    const store = storage();
    if (store) store.removeItem(PREP_SOURCE_KEY);
  } catch (_) {
    /* storage blocked */
  }
};

const readStored = () => {
  const store = storage();
  if (!store) return null;
  let raw = null;
  try {
    raw = store.getItem(PREP_SOURCE_KEY);
  } catch (_) {
    return null;
  }
  const source = parseStoredSource(raw, Date.now());
  if (raw && !source) removeStoredSource(); // expired or unreadable
  return source;
};

// Before MetaPixel reports, the signals that need no country.
const deniedNow = () => {
  if (report) return report.denied;
  try {
    const choices = readAdChoices();
    return gpcEnabled() || choices.optedOut || choices.consent === 'denied';
  } catch (_) {
    return false;
  }
};

/**
 * Called by components/MetaPixel.jsx whenever its answer changes. `allowed` is pixelAllowed for this
 * page; `denied` is adsDenied. Allowed: the page's source is kept, unless one is kept already (first
 * touch). Denied: the kept source is deleted. Anything else: nothing is read or written.
 */
export const syncPrepAttribution = ({ allowed, denied }) => {
  try {
    pageLanding(); // fixed now, before any in-app navigation changes the address
    report = { allowed: Boolean(allowed) && !denied, denied: Boolean(denied) };
    if (report.denied) removeStoredSource();
    else if (report.allowed && !readStored()) {
      const source = pageLanding();
      const store = storage();
      if (source && store) store.setItem(PREP_SOURCE_KEY, storedSourceRecord(source, Date.now()));
    }
  } catch (_) {
    /* storage blocked: the source stays in memory */
  }
  changed();
};

// The source the links carry now: the kept one when storage may be read (first touch), else this
// page's, without the click id when advertising is off.
export const currentSource = () => {
  try {
    const kept = report && report.allowed ? readStored() : null;
    const source = kept || pageLanding();
    return deniedNow() ? withoutClickId(source) : source;
  } catch (_) {
    return null;
  }
};

export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const getVersion = () => version;

// Tests only: forget this page's state, as a new page load would.
export const resetForTests = () => {
  landing = undefined;
  report = null;
  version = 0;
  listeners.clear();
};

// On a page load: the page's source is read while the address is still the one the page opened on,
// and a kept source is deleted as soon as a no-country signal says advertising is off (the inline
// script does the same before the app loads).
if (inBrowser()) {
  try {
    pageLanding();
    if (deniedNow()) removeStoredSource();
  } catch (_) {
    /* storage blocked */
  }
}
